import React from 'react';
import { Select, SelectItem } from '@nextui-org/react';

interface Option {
  value: string;
  label: string;
  kilos?: number;
}

interface MultiSelectWithActionsProps {
  label: string;
  placeholder?: string;
  options: Option[];
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  description?: string;
  disallowEmptySelection?: boolean;
  renderValue?: () => string;
  showSelectAll?: boolean;
  showDeselectAll?: boolean;
}

const MultiSelectWithActions: React.FC<MultiSelectWithActionsProps> = ({
  label,
  placeholder = 'Selecciona opciones',
  options,
  selectedKeys,
  onSelectionChange,
  size = 'md',
  className = 'w-full',
  isDisabled = false,
  isLoading = false,
  description,
  disallowEmptySelection = false,
  renderValue,
  showSelectAll = true,
  showDeselectAll = true,
}) => {
  const handleSelectionChange = (keys: any) => {
    const keysArray = Array.from(keys) as string[];

    // Handle "select all" and "deselect all" special options
    if (keysArray.includes('select_all')) {
      // Select all options except the special options
      const allOptionValues = options.map((option) => option.value);
      onSelectionChange(allOptionValues);
    } else if (keysArray.includes('deselect_all')) {
      onSelectionChange([]);
    } else {
      // Normal selection (filter out special options)
      const normalKeys = keysArray.filter(
        (key) => key !== 'select_all' && key !== 'deselect_all'
      );
      onSelectionChange(normalKeys);
    }
  };

  // Custom render value function
  const defaultRenderValue = () => {
    if (selectedKeys.length === 0) {
      return placeholder;
    }

    // Get the labels of selected options
    const selectedLabels = selectedKeys
      .map((key) => options.find((opt) => opt.value === key)?.label || key)
      .filter(Boolean);

    if (selectedKeys.length === options.length) {
      return `Todos seleccionados (${selectedKeys.length})`;
    }

    // Show up to 3 selected items, then add "y X más"
    if (selectedLabels.length <= 3) {
      return selectedLabels.join(', ');
    } else {
      const firstThree = selectedLabels.slice(0, 3);
      const remaining = selectedLabels.length - 3;
      return `${firstThree.join(', ')} y ${remaining} más`;
    }
  };

  return (
    <Select
      label={label}
      selectionMode='multiple'
      placeholder={placeholder}
      selectedKeys={new Set(selectedKeys)}
      onSelectionChange={handleSelectionChange}
      size={size}
      className={className}
      isDisabled={isDisabled}
      isLoading={isLoading}
      description={description}
      disallowEmptySelection={disallowEmptySelection}
      renderValue={renderValue || defaultRenderValue}
    >
      {[
        // Conditional special actions - only show if more than 3 options
        ...(showSelectAll && options.length > 3
          ? [
              <SelectItem
                key='select_all'
                value='select_all'
                className='text-blue-600 font-medium'
              >
                ✓ Seleccionar todos
              </SelectItem>,
            ]
          : []),
        ...(showDeselectAll && !disallowEmptySelection && options.length > 3
          ? [
              <SelectItem
                key='deselect_all'
                value='deselect_all'
                className='text-red-600 font-medium'
              >
                ✗ Deseleccionar todos
              </SelectItem>,
            ]
          : []),
        // Regular options
        ...options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <div className='flex justify-between items-center w-full'>
              <span>{option.label}</span>
              {option.kilos && (
                <span className='text-xs text-default-400'>
                  {Math.round(option.kilos).toLocaleString()} kg
                </span>
              )}
            </div>
          </SelectItem>
        )),
      ]}
    </Select>
  );
};

export default MultiSelectWithActions;
